/**
 * Base Mapper Utilities
 *
 * Provides utility functions for building API-specific mappers.
 * Each API should create its own response-specific mappers using these utilities.
 *
 * DO NOT use generic fromApi/toApi - instead create endpoint-specific mappers!
 */

/**
 * Convert a string from PascalCase to camelCase
 */
export const toCamelCase = (str: string): string => {
  return str.charAt(0).toLowerCase() + str.slice(1);
};

/**
 * Convert a string from camelCase to PascalCase
 */
export const toPascalCase = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Check if a field name indicates it contains JSON
 */
export const isJsonField = (key: string): boolean => {
  const patterns = ['Json', 'Config', 'Credentials', 'Metadata', 'Properties', 'Settings'];
  return patterns.some((pattern) => key.includes(pattern));
};

/**
 * Check if a string is valid JSON
 */
export const isJsonString = (str: string): boolean => {
  if (typeof str !== 'string' || str.trim().length === 0) return false;
  try {
    JSON.parse(str);
    return true;
  } catch {
    return false;
  }
};

/**
 * Transform object keys using a provided mapping
 *
 * @param obj - Object to transform
 * @param keyMap - Map of oldKey → newKey
 * @param options - Transformation options
 */
export const transformKeys = <T = unknown>(
  obj: unknown,
  keyMap: Record<string, string>,
  options: {
    parseJson?: boolean;
    recursive?: boolean;
  } = {}
): T => {
  const { parseJson = false, recursive = true } = options;

  // Handle null/undefined
  if (obj === null || obj === undefined) {
    return obj as T;
  }

  // Handle arrays
  if (Array.isArray(obj)) {
    return (recursive ? obj.map((item) => transformKeys(item, keyMap, options)) : obj) as T;
  }

  // Handle non-object types (primitives)
  if (typeof obj !== 'object') {
    return obj as T;
  }

  // Handle Date objects
  if (obj instanceof Date) {
    return obj as T;
  }

  // Transform object keys
  const result: Record<string, unknown> = {};

  Object.entries(obj as Record<string, unknown>).forEach(([key, value]) => {
    const newKey = keyMap[key] ?? key; // Use mapped key or keep original

    // Handle JSON string fields
    if (parseJson && typeof value === 'string' && isJsonField(key) && isJsonString(value)) {
      try {
        const parsed = JSON.parse(value);
        result[newKey] = recursive ? transformKeys(parsed, keyMap, options) : parsed;
      } catch {
        result[newKey] = value;
      }
    } else if (recursive && typeof value === 'object' && value !== null) {
      // Recursively transform nested objects/arrays
      result[newKey] = transformKeys(value, keyMap, options);
    } else {
      result[newKey] = value;
    }
  });

  return result as T;
};

/**
 * Stringify JSON fields back to strings
 *
 * @param obj - Object to process
 * @param keyMap - Map of keys to transform back
 */
export const stringifyJsonFields = <T = unknown>(
  obj: unknown,
  keyMap: Record<string, string>
): T => {
  if (obj === null || obj === undefined) {
    return obj as T;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => stringifyJsonFields(item, keyMap)) as T;
  }

  if (typeof obj !== 'object' || obj instanceof Date) {
    return obj as T;
  }

  const result: Record<string, unknown> = {};

  Object.entries(obj as Record<string, unknown>).forEach(([key, value]) => {
    const transformedKey = keyMap[key] ?? key;

    // If this is a JSON field and value is an object, stringify it
    if (
      isJsonField(transformedKey) &&
      typeof value === 'object' &&
      value !== null &&
      !(value instanceof Date)
    ) {
      result[transformedKey] = JSON.stringify(value);
    } else {
      result[transformedKey] = stringifyJsonFields(value, keyMap);
    }
  });

  return result as T;
};

/**
 * Create a reverse mapping (swap keys and values)
 */
export const reverseMap = (map: Record<string, string>): Record<string, string> => {
  return Object.fromEntries(Object.entries(map).map(([k, v]) => [v, k]));
};

/**
 * Transform array using a mapper function
 */
export const transformArray = <TInput, TOutput>(
  data: unknown,
  mapperFn: (item: TInput) => TOutput
): TOutput[] => {
  if (!Array.isArray(data)) {
    return [];
  }
  return data.map((item) => mapperFn(item as TInput));
};

/**
 * Identity function - returns data as-is
 * Useful for endpoints that don't need transformation
 */
export const identity = <T = unknown>(data: T): T => data;
